import { Component, computed, effect, inject, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  CementoCheckoutState,
  CementoTipoEntregaSelection,
  TIPO_DESPACHO_A_PISO,
  TIPO_DESPACHO_SOBRE_CAMION,
  TIPO_ENTREGA_DESPACHO,
  TIPO_ENTREGA_RETIRA_CLIENTE,
  TipoEntrega,
} from 'src/app/models/cemento-checkout';
import {
  CementCentro,
  CementProduct,
  CementProductDeliveryOption,
  CementProductDeliverySummaryItem,
  cementProductDeliverySummaryItems,
  cementProductNormalizeIncoterm,
} from 'src/app/models/cement-product';
import { Comuna } from 'src/app/models/comuna';
import { CementoCheckoutFlowService } from 'src/app/services/cemento-checkout-flow.service';
import { RadioOption } from 'src/app/shared/radio-option-group/radio-option-group.component';

interface DeliveryVariantOption extends RadioOption<number> {
  incoterm: string;
}

const INCOTERM_DESPACHO_A_PISO = 'ZO2';
const INCOTERM_DESPACHO_SOBRE_CAMION = 'ZO1';
const INCOTERM_RETIRA_CLIENTE = 'ZF5';

const DELIVERY_TYPE_LABELS: Record<TipoEntrega, string> = {
  [TIPO_ENTREGA_DESPACHO]: 'Despacho a dirección',
  [TIPO_ENTREGA_RETIRA_CLIENTE]: 'Retiro en Planta',
};

const DELIVERY_TYPE_SUBTEXTS: Record<TipoEntrega, string> = {
  [TIPO_ENTREGA_DESPACHO]: 'Recibe tu pedido en la dirección indicada.',
  [TIPO_ENTREGA_RETIRA_CLIENTE]: 'Retira tu pedido directamente en una planta o centro de distribución Cbb.',
};

const DESPACHO_VARIANT_LABELS: Record<string, string> = {
  [INCOTERM_DESPACHO_A_PISO]: 'Entrega a piso',
  [INCOTERM_DESPACHO_SOBRE_CAMION]: 'Entrega sobre camión',
};

const DESPACHO_VARIANT_SUBTEXTS: Record<string, string> = {
  [INCOTERM_DESPACHO_A_PISO]: 'El transportista descarga los sacos de cemento.',
  [INCOTERM_DESPACHO_SOBRE_CAMION]: 'El cliente es quien descarga los sacos de cemento.',
};

@Component({
  selector: 'app-cemento-tipo-entrega-section',
  templateUrl: './cemento-tipo-entrega-section.component.html',
  styleUrls: ['./cemento-tipo-entrega-section.component.css'],
})
export class CementoTipoEntregaSectionComponent {
  private readonly fb = inject(FormBuilder);
  private readonly flow = inject(CementoCheckoutFlowService);

  readonly validChange = output<boolean>();
  readonly selectionChange = output<string>();

  readonly TIPO_ENTREGA_DESPACHO = TIPO_ENTREGA_DESPACHO;
  readonly TIPO_ENTREGA_RETIRA_CLIENTE = TIPO_ENTREGA_RETIRA_CLIENTE;

  readonly compareComunaById = (a: Comuna | null, b: Comuna | null): boolean =>
    (a?.id ?? null) === (b?.id ?? null);

  form!: FormGroup;
  private readonly checkoutState = toSignal(this.flow.checkoutState, {
    initialValue: {} as CementoCheckoutState,
  });

  readonly selectedProduct = computed<CementProduct | undefined>(
    () => this.checkoutState()?.producto?.product
  );

  readonly deliveryOptions = computed<CementProductDeliveryOption[]>(
    () => this.selectedProduct()?.delivery_options ?? []
  );

  readonly deliverySummaryItems = computed<CementProductDeliverySummaryItem[]>(() => {
    const product = this.selectedProduct();
    if (!product) {
      return [];
    }
    return cementProductDeliverySummaryItems(product);
  });

  readonly tipoEntregaOptions = computed<RadioOption<string>[]>(() => {
    const summaryItems = this.deliverySummaryItems();
    if (summaryItems.length === 0) {
      return [];
    }

    const despachoVariants = [INCOTERM_DESPACHO_A_PISO, INCOTERM_DESPACHO_SOBRE_CAMION].map((incoterm) =>
      this.summaryItemByIncoterm(incoterm)
    );
    const despachoAvailableCount = despachoVariants.filter((item) => this.isSummaryVariantAvailable(item)).length;

    const retiroVariant = this.summaryItemByIncoterm(INCOTERM_RETIRA_CLIENTE);
    const retiroAvailable = this.isSummaryVariantAvailable(retiroVariant);
    const retiroDisabled = retiroAvailable === false;

    const despachoUnavailable = despachoAvailableCount === 0;
    return [
      {
        value: TIPO_ENTREGA_DESPACHO,
        label: DELIVERY_TYPE_LABELS[TIPO_ENTREGA_DESPACHO],
        disabled: despachoUnavailable,
        helperText: despachoUnavailable
          ? 'No disponible actualmente'
          : DELIVERY_TYPE_SUBTEXTS[TIPO_ENTREGA_DESPACHO],
      },
      {
        value: TIPO_ENTREGA_RETIRA_CLIENTE,
        label: DELIVERY_TYPE_LABELS[TIPO_ENTREGA_RETIRA_CLIENTE],
        disabled: retiroDisabled,
        helperText: retiroDisabled
          ? 'No disponible actualmente'
          : DELIVERY_TYPE_SUBTEXTS[TIPO_ENTREGA_RETIRA_CLIENTE],
      },
    ];
  });

  readonly despachoOptions = computed<DeliveryVariantOption[]>(() => {
    const despachoIncoterms = [INCOTERM_DESPACHO_A_PISO, INCOTERM_DESPACHO_SOBRE_CAMION];
    return despachoIncoterms.map((incoterm) => {
      const summaryItem = this.summaryItemByIncoterm(incoterm);
      const available = this.isSummaryVariantAvailable(summaryItem);
      const disabled = available === false;
      return {
        value: this.tipoDespachoForIncoterm(incoterm),
        label: DESPACHO_VARIANT_LABELS[incoterm] ?? incoterm,
        incoterm,
        disabled,
        helperText: disabled
          ? 'No disponible actualmente'
          : DESPACHO_VARIANT_SUBTEXTS[incoterm],
      };
    });
  });

  readonly selectedCentro = computed<CementCentro | null>(
    () => this.selectedProduct()?.centro ?? null
  );

  readonly comunas = computed<Comuna[]>(() => {
    const product = this.selectedProduct();
    const geo = product?.centro?.datos_geograficos ?? [];
    const options = geo.map((g) => ({ id: g.id, name: g.name, code: g.code }));
    if (options.length > 0) {
      return options;
    }
    const savedComuna = this.checkoutState()?.tipoEntrega?.comuna;
    return savedComuna ? [savedComuna] : [];
  });

  constructor() {
    this.initForm();
    this.setupTipoEntregaChange();
    this.syncSelectionFromState();
    this.syncProductSelectedIncoterm();
    this.emitSelectionFingerprint();
  }

  private initForm(): void {
    this.form = this.fb.group({
      tipoEntrega: [null as string | null, [Validators.required]],
      tipoDespacho: [null as number | null],
      comuna: [null as Comuna | null],
    });

    this.form.statusChanges.subscribe(() => {
      this.validChange.emit(this.isFormValid());
    });

    this.form.valueChanges.subscribe(() => {
      this.emitCurrentFingerprint();
    });

    this.validChange.emit(this.isFormValid());
  }

  private emitCurrentFingerprint(): void {
    const selection = this.getSelection();
    const fingerprint = selection
      ? [
          selection.tipoEntrega ?? '',
          selection.tipoDespacho ?? '',
          selection.incoterm ?? '',
          selection.comuna?.code ?? '',
        ].join('|')
      : '';
    this.selectionChange.emit(fingerprint);
  }

  private setupTipoEntregaChange(): void {
    this.form.get('tipoEntrega')?.valueChanges.subscribe((tipo) => {
      const comunaCtrl = this.form.get('comuna');
      const tipoDespachoCtrl = this.form.get('tipoDespacho');

      if (tipo === TIPO_ENTREGA_RETIRA_CLIENTE && this.comunas().length > 0) {
        comunaCtrl?.setValidators([Validators.required]);
      } else {
        comunaCtrl?.clearValidators();
        comunaCtrl?.setValue(null, { emitEvent: false });
      }
      comunaCtrl?.updateValueAndValidity({ emitEvent: false });

      if (tipo === TIPO_ENTREGA_DESPACHO) {
        // La modalidad debe ser una eleccion explicita, incluso cuando solo
        // exista una alternativa disponible.
        tipoDespachoCtrl?.setValue(null, { emitEvent: false });
      } else {
        tipoDespachoCtrl?.setValue(null, { emitEvent: false });
      }
    });
  }

  private syncSelectionFromState(): void {
    effect(() => {
      const saved = this.checkoutState()?.tipoEntrega;
      const product = this.selectedProduct();
      if (!product) {
        return;
      }

      const availableTypes = this.tipoEntregaOptions().filter((option) => !option.disabled);
      const currentTipo = this.form.get('tipoEntrega')?.value as string | null;
      const savedTipo = saved?.tipoEntrega;

      const nextTipo = this.resolveNextTipoEntrega(savedTipo, currentTipo, availableTypes);

      if (this.form.get('tipoEntrega')?.value !== nextTipo) {
        this.form.get('tipoEntrega')?.setValue(nextTipo, { emitEvent: true });
      }

      if (
        nextTipo === TIPO_ENTREGA_DESPACHO &&
        (saved?.incoterm != null || saved?.tipoDespacho != null)
      ) {
        this.applyDespachoSelection(product, saved?.incoterm, saved?.tipoDespacho);
      }

      if (nextTipo === TIPO_ENTREGA_RETIRA_CLIENTE) {
        this.applyRetiroSelection(product);
      }

      if (saved?.comuna && this.form.get('comuna')?.value?.code !== saved.comuna.code) {
        this.form.get('comuna')?.setValue(saved.comuna, { emitEvent: false });
      }

      this.validChange.emit(this.isFormValid());
    });
  }

  private syncProductSelectedIncoterm(): void {
    effect(() => {
      const product = this.selectedProduct();
      if (!product) {
        return;
      }
      const tipoEntrega = this.form.get('tipoEntrega')?.value as TipoEntrega | null;
      if (tipoEntrega === TIPO_ENTREGA_DESPACHO) {
        const selectedIncoterm = this.getSelectedIncotermForDespacho();
        if (selectedIncoterm) {
          product.selected_incoterm = selectedIncoterm;
        }
      } else if (tipoEntrega === TIPO_ENTREGA_RETIRA_CLIENTE) {
        const retiroOption = this.firstAvailableRetiroOption();
        if (retiroOption) {
          product.selected_incoterm = cementProductNormalizeIncoterm(retiroOption.incoterms);
        }
      }
    });
  }

  private emitSelectionFingerprint(): void {
    effect(() => {
      this.selectedProduct();
      this.emitCurrentFingerprint();
    });
  }

  private tipoDespachoForIncoterm(incoterm: string): number {
    return cementProductNormalizeIncoterm(incoterm) === INCOTERM_DESPACHO_SOBRE_CAMION
      ? TIPO_DESPACHO_SOBRE_CAMION
      : TIPO_DESPACHO_A_PISO;
  }

  private deliveryOptionByIncoterm(incoterm: string): CementProductDeliveryOption | null {
    const normalized = cementProductNormalizeIncoterm(incoterm);
    if (!normalized) {
      return null;
    }
    return (
      this.deliveryOptions().find(
        (option) => cementProductNormalizeIncoterm(option.incoterms) === normalized
      ) ?? null
    );
  }

  private summaryItemByIncoterm(incoterm: string): CementProductDeliverySummaryItem | null {
    const normalized = cementProductNormalizeIncoterm(incoterm);
    if (!normalized) {
      return null;
    }
    return this.deliverySummaryItems().find((item) => item.incoterm === normalized) ?? null;
  }

  private isSummaryVariantAvailable(item: CementProductDeliverySummaryItem | null): boolean {
    if (!item?.available) {
      return false;
    }
    return this.deliveryOptionByIncoterm(item.incoterm) !== null;
  }

  private despachoHelperText(
    availableCount: number,
    totalVariants: number
  ): string | undefined {
    if (availableCount === 0) {
      return 'No disponible actualmente';
    }
    if (availableCount < totalVariants) {
      return 'Algunas variantes no están disponibles para esta comuna';
    }
    return undefined;
  }

  private resolveNextTipoEntrega(
    savedTipo: string | undefined,
    currentTipo: string | null,
    availableTypes: RadioOption<string>[]
  ): string | null {
    const hasSavedTipo = Boolean(
      savedTipo && availableTypes.some((option) => option.value === savedTipo)
    );
    if (hasSavedTipo) {
      return savedTipo ?? null;
    }
    const hasCurrentTipo = Boolean(
      currentTipo && availableTypes.some((option) => option.value === currentTipo)
    );
    if (hasCurrentTipo) {
      return currentTipo;
    }
    // Sin datos guardados ni selección previa: el usuario elige explícitamente.
    return null;
  }

  private applyDespachoSelection(
    product: CementProduct,
    savedIncoterm?: string | null,
    savedTipoDespacho?: number | null
  ): void {
    const matched = this.resolveSavedDespachoOption(savedIncoterm, savedTipoDespacho);
    if (!matched) {
      return;
    }
    if (this.form.get('tipoDespacho')?.value !== matched.value) {
      this.form.get('tipoDespacho')?.setValue(matched.value, { emitEvent: false });
    }
    product.selected_incoterm = matched.incoterm;
  }

  private applyRetiroSelection(product: CementProduct): void {
    const retiroOption = this.firstAvailableRetiroOption();
    if (!retiroOption) {
      return;
    }
    product.selected_incoterm = cementProductNormalizeIncoterm(retiroOption.incoterms);
  }

  private firstAvailableRetiroOption(): CementProductDeliveryOption | null {
    const summaryRetiro = this.summaryItemByIncoterm(INCOTERM_RETIRA_CLIENTE);
    if (!this.isSummaryVariantAvailable(summaryRetiro)) {
      return null;
    }
    return this.deliveryOptionByIncoterm(INCOTERM_RETIRA_CLIENTE);
  }

  private findDespachoOptionByIncoterm(incoterm?: string | null): DeliveryVariantOption | null {
    const normalized = cementProductNormalizeIncoterm(incoterm);
    if (!normalized) {
      return null;
    }
    return this.despachoOptions().find((option) => option.incoterm === normalized) ?? null;
  }

  private resolveSavedDespachoOption(
    savedIncoterm?: string | null,
    savedTipoDespacho?: number | null
  ): DeliveryVariantOption | null {
    const byIncoterm = this.findDespachoOptionByIncoterm(savedIncoterm);
    if (byIncoterm && !byIncoterm.disabled) {
      return byIncoterm;
    }
    if (savedTipoDespacho != null) {
      return (
        this.despachoOptions().find(
          (option) => option.value === savedTipoDespacho && !option.disabled
        ) ?? null
      );
    }
    return null;
  }

  private getSelectedIncotermForDespacho(): string {
    const selectedTipoDespacho = this.normalizeTipoDespachoValue(
      this.form.get('tipoDespacho')?.value
    );
    const matched = this.despachoOptions().find(
      (option) => option.value === selectedTipoDespacho && !option.disabled
    );
    return matched?.incoterm ?? '';
  }

  private normalizeTipoDespachoValue(value: unknown): number | null {
    if (value == null || value === '') {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private isFormValid(): boolean {
    const tipoEntrega = this.form.get('tipoEntrega')?.value as TipoEntrega | null;
    if (!tipoEntrega) {
      return false;
    }
    if (tipoEntrega === TIPO_ENTREGA_DESPACHO) {
      return !!this.getSelectedIncotermForDespacho();
    }
    if (tipoEntrega === TIPO_ENTREGA_RETIRA_CLIENTE) {
      if (!this.firstAvailableRetiroOption()) {
        return false;
      }
      if (this.comunas().length > 0) {
        return this.form.get('comuna')?.valid ?? false;
      }
      return true;
    }
    return false;
  }

  resetSelection(): void {
    this.form.reset();
    this.validChange.emit(false);
    this.selectionChange.emit('');
  }

  getSelection(): CementoTipoEntregaSelection | null {
    if (!this.isFormValid()) {
      return null;
    }

    const tipoEntrega = this.form.get('tipoEntrega')?.value as TipoEntrega;
    const comuna = this.form.get('comuna')?.value as Comuna | null;

    if (tipoEntrega === TIPO_ENTREGA_RETIRA_CLIENTE) {
      const retiroOption = this.firstAvailableRetiroOption();
      const incoterm = cementProductNormalizeIncoterm(retiroOption?.incoterms);
      if (!incoterm) {
        return null;
      }
      return {
        tipoEntrega,
        comuna,
        incoterm,
      };
    }

    const tipoDespacho = this.normalizeTipoDespachoValue(
      this.form.get('tipoDespacho')?.value
    );
    const incoterm = this.getSelectedIncotermForDespacho();
    if (!incoterm || tipoDespacho == null) {
      return null;
    }
    return {
      tipoEntrega,
      tipoDespacho,
      incoterm,
    };
  }
}
